import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Link } from "react-router-dom";
import { Wallet, Megaphone, Package, HeartPulse, RefreshCw, Loader2, AlertCircle, ExternalLink, Info, Truck } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";

// ---------------- Tipler ----------------

type CatTotals = Partial<Record<string, number>>;
interface PnlRow {
  month: string; cats: CatTotals; netSales: number; etsyCosts: number; etsyNet: number;
  productCost: number; costCoverage: { known: number; total: number }; profit: number; margin: number | null; orders: number;
}
interface PnlData {
  currency: string;
  months: PnlRow[];
  totals: Omit<PnlRow, "month"> & { margin: number | null };
  transfers: Array<{ date: string; type: string; amount: number }>;
  unknownTypes: Array<{ type: string; amount: number }>;
  labels: Record<string, string>;
}
interface AdsData {
  currency: string; days: number;
  totals: {
    sales: number; orders: number; ads: number; adsVat: number; offsite: number; roas: number | null; acos: number | null;
    adsShareOfSales: number | null; contribution: number; marginBeforeAds: number | null; breakEvenRoas: number | null;
    offsiteOrders: number; offsiteRevenue: number;
  };
  weekly: Array<{ week: string; sales: number; orders: number; ads: number; offsite: number; roas: number | null }>;
  offsiteOrders: Array<{ receiptId: string; fee: number; revenue: number | null; rate: number | null }>;
  costCoverage: { known: number; total: number };
}
interface ListingProfit {
  listingId: string; title: string; imageUrl?: string | null; analysisId?: string | null; units: number; orders: number; revenue: number; fees: number; listingFees: number;
  offsite: number; cost: number; costKnown: boolean; profit: number; margin: number | null; profitPerUnit: number | null;
}
interface ListingProfitData { days: number; currency: string; listings: ListingProfit[]; adsNote: string }
type Health = "star" | "converting" | "interest_no_sale" | "low_conversion" | "low_visibility" | "dead" | "new";
interface HealthRow {
  listingId: string; title: string; imageUrl?: string | null; analysisId?: string | null; url: string; price: number | null; quantity: number | null; ageDays: number;
  views: number; viewsPerDay: number; favorites: number; favRate: number | null; sales30: number; sales90: number;
  revenue90: number; lastSale: string | null; conv90: number | null; listingFees90: number; autoRenew: boolean | null;
  expiresAt: string | null; health: Health;
  sku?: string | null; shippingProfile?: string | null; group?: string; twins?: number; etsyImage?: string | null;
}
interface HealthData {
  listings: HealthRow[]; counts: Partial<Record<Health, number>>;
  labels: Record<Health, { label: string; advice: string }>;
  shop: { medianConv: number | null; medianViewsPerDay: number; activeListings: number };
}

async function getJson<T>(path: string, refresh: boolean): Promise<T> {
  const sep = path.includes("?") ? "&" : "?";
  const res = await fetch(`${getBackendUrl()}/api/finance/${path}${refresh ? `${sep}refresh=1` : ""}`, { headers: authHeaders() });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Yüklenemedi (${res.status})`);
  return json.data as T;
}

/** "Etsy'den yenile" yalnizca ilk sorguda onbellegi atlatsin; donem degisince tekrar cekmesin. */
function useRefreshOnce(refreshKey: number) {
  const used = useRef(0);
  return () => {
    const r = refreshKey > used.current;
    used.current = refreshKey;
    return r;
  };
}

// ---------------- Yardimcilar ----------------

const fmtMoney = (v: number | null | undefined, cur = "USD") =>
  v == null ? "–" : new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(v);
const fmtNum = (v: number | null | undefined, d = 0) => (v == null ? "–" : v.toLocaleString("tr-TR", { maximumFractionDigits: d, minimumFractionDigits: d }));
const fmtPct = (v: number | null | undefined) => (v == null ? "–" : `%${v.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`);
const monthLabel = (m: string) => new Date(m + "-01T00:00:00Z").toLocaleDateString("tr-TR", { month: "short", year: "numeric", timeZone: "UTC" });
const signColor = (v: number | null | undefined) => (v == null ? "text-gray-900" : v < 0 ? "text-red-600" : "text-gray-900");

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: React.ReactNode; tone?: "good" | "bad" }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className={clsx("mt-1 text-2xl font-bold", tone === "bad" ? "text-red-600" : tone === "good" ? "text-emerald-700" : "text-gray-900")}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

// Ilan gorseli (panelden); uzerine gelince buyuk onizleme. Tablo yatay
// kaydirilabildigi icin onizleme sabit konumlu (kesilmesin).
function Thumb({ src, alt, large = false }: { src: string | null | undefined; alt: string; large?: boolean }) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const box = large ? "h-16 w-16" : "h-10 w-10";
  if (!src) return <div className={clsx(box, "shrink-0 rounded-lg border border-gray-100 bg-gray-50")} />;
  const url = /^(https?:|data:)/.test(src) ? src : `${getBackendUrl()}${src.startsWith("/") ? "" : "/"}${src}`;
  const SIZE = large ? Math.min(480, window.innerHeight - 16) : 320;
  return (
    <>
      <img src={url} alt={alt} loading="lazy"
        className={clsx(box, "shrink-0 cursor-zoom-in rounded-lg border border-gray-200 bg-white object-cover")}
        onMouseEnter={(e) => { const b = e.currentTarget.getBoundingClientRect(); setPos({ x: b.right + 8, y: b.top + b.height / 2 }); }}
        onMouseLeave={() => setPos(null)} />
      {pos && (
        <img src={url} alt="" aria-hidden
          className="pointer-events-none fixed z-50 rounded-lg border border-gray-200 bg-white object-contain shadow-2xl"
          style={{
            width: SIZE, height: SIZE,
            left: Math.min(pos.x, window.innerWidth - SIZE - 8),
            top: Math.max(8, Math.min(pos.y - SIZE / 2, window.innerHeight - SIZE - 8)),
          }} />
      )}
    </>
  );
}

// Ilanin dogdugu ilham analizi (satis kaniti orada) — sonuc takibi
function IlhamLink({ id }: { id: string }) {
  return (
    <Link to={`/analyses/${id}`} title="İlham analizi ve satış kanıtı" className="shrink-0 rounded border border-emerald-200 bg-emerald-50 px-1 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100">
      İlham
    </Link>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
      <div>{children}</div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center gap-2 py-20 text-sm text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin" /> Etsy'den veriler çekiliyor…
    </div>
  );
}
function ErrorBox({ error }: { error: unknown }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
      <AlertCircle className="h-5 w-5 text-red-500" /> {(error as Error)?.message || "Yüklenemedi"}
    </div>
  );
}

function Select<T extends string | number>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<[T, string]> }) {
  return (
    <select
      value={String(value)}
      onChange={(e) => onChange((typeof value === "number" ? Number(e.target.value) : e.target.value) as T)}
      className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700"
    >
      {options.map(([v, l]) => <option key={String(v)} value={String(v)}>{l}</option>)}
    </select>
  );
}

// ---------------- Kar-zarar ----------------

const PNL_LINES: Array<{ key: string; indent?: boolean }> = [
  { key: "sales" }, { key: "refunds", indent: true }, { key: "sales_tax", indent: true },
];
const COST_LINES = ["fees_order", "fees_listing", "ads", "offsite_ads", "subscription", "shipping_labels", "vat", "other_fees", "credits"];

export function PnlTab({ refreshKey }: { refreshKey: number }) {
  const shopId = useActiveShopId();
  const takeRefresh = useRefreshOnce(refreshKey);
  const [months, setMonths] = useState(6);
  const q = useQuery({ queryKey: ["finance-pnl", shopId, months, refreshKey], queryFn: () => getJson<PnlData>(`pnl?months=${months}`, takeRefresh()) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const cur = d.currency;
  const t = d.totals;
  const cov = t.costCoverage;
  const cell = (v: number | undefined, cls = "") => (
    <td className={clsx("px-3 py-1.5 text-right tabular-nums", (v ?? 0) < 0 ? "text-red-600" : "text-gray-800", cls)}>{v ? fmtMoney(v, cur) : "–"}</td>
  );
  const row = (label: string, get: (r: PnlRow | PnlData["totals"]) => number | undefined, opts: { bold?: boolean; indent?: boolean; border?: boolean } = {}) => (
    <tr className={clsx(opts.border && "border-t border-gray-200", opts.bold && "font-semibold")}>
      <td className={clsx("px-3 py-1.5 text-gray-700", opts.indent && "pl-7 text-gray-500")}>{label}</td>
      {d.months.map((m) => <td key={m.month} className="p-0">{cell(get(m))}</td>)}
      <td className="bg-gray-50 p-0">{cell(get(t), "font-semibold")}</td>
    </tr>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Select value={months} onChange={setMonths} options={[[3, "Son 3 ay"], [6, "Son 6 ay"], [12, "Son 12 ay"]]} />
        <span className="text-xs text-gray-500">{t.orders} sipariş · Kaynak: Etsy ödeme hesabı (ledger)</span>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Net satış" value={fmtMoney(t.netSales, cur)} sub="Brüt − iade − alıcı vergisi" />
        <Tile label="Etsy kesintileri" value={fmtMoney(t.etsyCosts, cur)} sub={t.netSales > 0 ? `Satışın ${fmtPct((t.etsyCosts / t.netSales) * 100)}'i` : undefined} />
        <Tile label="Ürün + kargo maliyeti" value={fmtMoney(t.productCost, cur)} sub={`${cov.known}/${cov.total} siparişin maliyeti biliniyor`} />
        <Tile label="Net kâr" value={fmtMoney(t.profit, cur)} sub={`Marj ${fmtPct(t.margin)}`} tone={t.profit < 0 ? "bad" : "good"} />
      </div>
      {cov.known < cov.total && (
        <Note>{cov.total - cov.known} siparişin ürün/kargo maliyeti bulunamadı; kâr bu siparişler için maliyetsiz hesaplandı. Siparişler sayfasından elle girilebilir.</Note>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Kalem</th>
              {d.months.map((m) => <th key={m.month} className="px-3 py-2 text-right font-medium">{monthLabel(m.month)}</th>)}
              <th className="bg-gray-100 px-3 py-2 text-right font-medium">Toplam</th>
            </tr>
          </thead>
          <tbody>
            {PNL_LINES.map((l) => row(d.labels[l.key], (r) => r.cats[l.key], { indent: l.indent }))}
            {row("Net satış", (r) => r.netSales, { bold: true, border: true })}
            {COST_LINES.filter((k) => d.months.some((m) => m.cats[k])).map((k) => row(d.labels[k], (r) => r.cats[k], { indent: true }))}
            {row("Etsy kesintileri toplamı", (r) => -r.etsyCosts, { bold: true, border: true })}
            {d.months.some((m) => m.cats.other_income) && row(d.labels.other_income, (r) => r.cats.other_income, { indent: true })}
            {row("Etsy'den kalan", (r) => r.etsyNet, { bold: true, border: true })}
            {row("Ürün + kargo maliyeti", (r) => -r.productCost, { indent: true })}
            {row("Net kâr", (r) => r.profit, { bold: true, border: true })}
            <tr className="text-xs text-gray-500">
              <td className="px-3 py-1.5">Marj</td>
              {d.months.map((m) => <td key={m.month} className="px-3 py-1.5 text-right">{fmtPct(m.margin)}</td>)}
              <td className="bg-gray-50 px-3 py-1.5 text-right">{fmtPct(t.margin)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Para hareketleri (kâr-zarara dahil değil)</h3>
          {d.transfers.length === 0 ? <p className="text-xs text-gray-500">Bu dönemde transfer yok.</p> : (
            <table className="w-full text-xs">
              <tbody>
                {d.transfers.slice(0, 12).map((x, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-1 text-gray-500">{x.date}</td>
                    <td className="py-1 text-gray-700">{x.type.replace(/_/g, " ")}</td>
                    <td className={clsx("py-1 text-right tabular-nums", signColor(x.amount))}>{fmtMoney(x.amount, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="space-y-2">
          <Note>Ay, kaydın Etsy'ye düştüğü tarihe göredir. Satış tutarı ödeme alındığında, ücretler sipariş anında yazılır; ay sınırındaki siparişler iki aya bölünebilir.</Note>
          <Note>Etsy kredileri (Etsy Plus) kesintilerden düşülmüştür. Ürün maliyeti sırası: siparişte elle girilen → kayıtlı sipariş dökümü → tedarikçi kataloğu.</Note>
          {d.unknownTypes.length > 0 && (
            <Note>"Diğer" altında toplanan kayıt türleri: {d.unknownTypes.map((u) => `${u.type} (${fmtMoney(u.amount, cur)})`).join(", ")}</Note>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Reklam ----------------

const C_SALES = "#2a78d6";
const C_ADS = "#eb6834";

function AdsChart({ weeks, cur }: { weeks: AdsData["weekly"]; cur: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...weeks.map((w) => Math.max(w.sales, w.ads + w.offsite)));
  const H = 180;
  return (
    <div>
      <div className="mb-3 flex items-center gap-4 text-xs text-gray-600">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_SALES }} /> Satış</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_ADS }} /> Reklam harcaması (Etsy Ads + Offsite)</span>
      </div>
      <div className="relative">
        <div className="flex items-end gap-1.5 border-b border-gray-200" style={{ height: H }}>
          {weeks.map((w, i) => {
            const spend = w.ads + w.offsite;
            return (
              <div key={w.week} className="relative flex h-full flex-1 items-end justify-center gap-[2px]"
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                <div className="w-1/2 max-w-[18px] rounded-t" style={{ height: `${(w.sales / max) * 100}%`, minHeight: w.sales > 0 ? 2 : 0, background: C_SALES, opacity: hover === null || hover === i ? 1 : 0.45 }} />
                <div className="w-1/2 max-w-[18px] rounded-t" style={{ height: `${(spend / max) * 100}%`, minHeight: spend > 0 ? 2 : 0, background: C_ADS, opacity: hover === null || hover === i ? 1 : 0.45 }} />
                {hover === i && (
                  <div className="absolute bottom-full z-10 mb-2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
                    <div className="font-medium">{w.week} haftası</div>
                    <div>Satış: {fmtMoney(w.sales, cur)} ({w.orders} sipariş)</div>
                    <div>Etsy Ads: {fmtMoney(w.ads, cur)}</div>
                    {w.offsite > 0 && <div>Offsite Ads: {fmtMoney(w.offsite, cur)}</div>}
                    <div>ROAS: {w.roas == null ? "–" : `${fmtNum(w.roas, 2)}x`}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-1 flex gap-1.5">
          {weeks.map((w, i) => (
            <div key={w.week} className="flex-1 text-center text-[10px] text-gray-400">
              {i % Math.max(1, Math.ceil(weeks.length / 8)) === 0 ? new Date(w.week + "T00:00:00Z").toLocaleDateString("tr-TR", { day: "numeric", month: "short", timeZone: "UTC" }) : ""}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdsTab({ refreshKey }: { refreshKey: number }) {
  const shopId = useActiveShopId();
  const takeRefresh = useRefreshOnce(refreshKey);
  const [days, setDays] = useState(90);
  const [showTable, setShowTable] = useState(false);
  const q = useQuery({ queryKey: ["finance-ads", shopId, days, refreshKey], queryFn: () => getJson<AdsData>(`ads?days=${days}`, takeRefresh()) });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const t = d.totals;
  const cur = d.currency;
  const spend = t.ads + t.adsVat;
  const verdict =
    t.roas == null ? null
      : t.breakEvenRoas == null ? { tone: "bad" as const, text: "Katkı marjı sıfırın altında: reklam öncesi bile zarar var, önce fiyat/maliyeti düzelt." }
      : t.roas >= t.breakEvenRoas ? { tone: "good" as const, text: `ROAS ${fmtNum(t.roas, 2)}x, başabaş ${fmtNum(t.breakEvenRoas, 2)}x üzerinde: reklam kâr getiriyor.` }
      : { tone: "bad" as const, text: `ROAS ${fmtNum(t.roas, 2)}x, başabaş ${fmtNum(t.breakEvenRoas, 2)}x altında: her reklam dolarında zarar ediliyor. Bütçeyi düşür veya zayıf ilanları reklamdan çıkar.` };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Select value={days} onChange={setDays} options={[[30, "Son 30 gün"], [60, "Son 60 gün"], [90, "Son 90 gün"], [180, "Son 180 gün"]]} />
        <span className="text-xs text-gray-500">Kaynak: ledger'daki günlük Etsy Ads ("prolist") ve Offsite Ads kayıtları</span>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Etsy Ads harcaması" value={fmtMoney(spend, cur)} sub={`${fmtMoney(t.ads, cur)} + KDV ${fmtMoney(t.adsVat, cur)}`} />
        <Tile label="ROAS (satış / harcama)" value={t.roas == null ? "–" : `${fmtNum(t.roas, 2)}x`} sub={`ACOS ${fmtPct(t.acos)} · ${fmtMoney(t.sales, cur)} satış`}
          tone={t.roas != null && t.breakEvenRoas != null ? (t.roas >= t.breakEvenRoas ? "good" : "bad") : undefined} />
        <Tile label="Başabaş ROAS" value={t.breakEvenRoas == null ? "–" : `${fmtNum(t.breakEvenRoas, 2)}x`} sub={`Reklam öncesi katkı marjı ${fmtPct(t.marginBeforeAds)}`} />
        <Tile label="Offsite Ads" value={fmtMoney(t.offsite, cur)} sub={`${t.offsiteOrders} sipariş · ${fmtMoney(t.offsiteRevenue, cur)} ciro`} />
      </div>
      {verdict && (
        <div className={clsx("rounded-lg border px-3 py-2 text-sm", verdict.tone === "good" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800")}>
          {verdict.text}
        </div>
      )}
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Haftalık satış ve reklam harcaması</h3>
          <button onClick={() => setShowTable((v) => !v)} className="text-xs text-gray-500 underline-offset-2 hover:underline">{showTable ? "Grafik" : "Tablo"}</button>
        </div>
        {showTable ? (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500"><tr>
              <th className="py-1 text-left font-medium">Hafta</th><th className="py-1 text-right font-medium">Satış</th><th className="py-1 text-right font-medium">Sipariş</th>
              <th className="py-1 text-right font-medium">Etsy Ads</th><th className="py-1 text-right font-medium">Offsite</th><th className="py-1 text-right font-medium">ROAS</th>
            </tr></thead>
            <tbody>{d.weekly.map((w) => (
              <tr key={w.week} className="border-t border-gray-100 tabular-nums">
                <td className="py-1">{w.week}</td><td className="py-1 text-right">{fmtMoney(w.sales, cur)}</td><td className="py-1 text-right">{w.orders}</td>
                <td className="py-1 text-right">{fmtMoney(w.ads, cur)}</td><td className="py-1 text-right">{fmtMoney(w.offsite, cur)}</td>
                <td className="py-1 text-right">{w.roas == null ? "–" : `${fmtNum(w.roas, 2)}x`}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <AdsChart weeks={d.weekly} cur={cur} />}
      </div>
      <div className="space-y-2">
        <Note>Etsy reklam API'si sunmuyor: harcama mağaza bazında ve günlük alınabiliyor; hangi ilanın ne kadar harcadığı/tıklandığı Etsy Ads panelinde görünür. ROAS burada tüm satışlara göre hesaplanır (Etsy'nin "reklamdan gelen satış" ölçüsünden geniştir).</Note>
        <Note>Başabaş ROAS = 1 / katkı marjı. Katkı marjı sipariş başına değişen maliyetleri (işlem/ödeme ücretleri, ürün ve kargo) düşer; abonelik ve listeleme gibi sabit giderleri içermez. {d.costCoverage.known}/{d.costCoverage.total} siparişin maliyeti biliniyor.</Note>
      </div>
    </div>
  );
}

// ---------------- Ilan karliligi ----------------

type LpSort = "profit" | "revenue" | "margin" | "units";

export function ListingProfitTab({ refreshKey }: { refreshKey: number }) {
  const shopId = useActiveShopId();
  const takeRefresh = useRefreshOnce(refreshKey);
  const [days, setDays] = useState(90);
  const [onlySold, setOnlySold] = useState(true);
  const [sort, setSort] = useState<LpSort>("profit");
  const q = useQuery({ queryKey: ["finance-listings", shopId, days, refreshKey], queryFn: () => getJson<ListingProfitData>(`listings?days=${days}`, takeRefresh()) });
  const rows = useMemo(() => {
    const list = (q.data?.listings ?? []).filter((l) => !onlySold || l.units > 0);
    return [...list].sort((a, b) => ((b[sort] ?? -1e9) as number) - ((a[sort] ?? -1e9) as number));
  }, [q.data, onlySold, sort]);
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const cur = d.currency;
  const unsold = d.listings.filter((l) => l.units === 0);
  const unsoldFees = unsold.reduce((s, l) => s + l.listingFees, 0);
  const th = (k: LpSort | null, label: string) => (
    <th className={clsx("px-3 py-2 text-right font-medium", k && "cursor-pointer hover:text-gray-800", sort === k && "text-gray-900")} onClick={() => k && setSort(k)}>{label}</th>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={days} onChange={setDays} options={[[30, "Son 30 gün"], [90, "Son 90 gün"], [180, "Son 180 gün"], [365, "Son 365 gün"]]} />
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={onlySold} onChange={(e) => setOnlySold(e.target.checked)} /> Yalnız satanlar
        </label>
        <span className="ml-auto text-xs text-gray-500">
          {unsold.length} ilan satmadı, listeleme/yenileme ücreti {fmtMoney(unsoldFees, cur)}
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">İlan</th>
              {th("units", "Adet")}{th("revenue", "Ciro")}{th(null, "Etsy ücreti")}{th(null, "Listeleme")}{th(null, "Offsite")}
              {th(null, "Maliyet")}{th("profit", "Kâr")}{th("margin", "Marj")}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-500">Bu dönemde satış yok.</td></tr>}
            {rows.map((l) => (
              <tr key={l.listingId} className="border-t border-gray-100 tabular-nums">
                <td className="max-w-[400px] px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <Thumb src={l.imageUrl} alt={l.title} />
                    {l.analysisId && <IlhamLink id={l.analysisId} />}
                    <a href={`https://www.etsy.com/listing/${l.listingId}`} target="_blank" rel="noreferrer" className="line-clamp-2 min-w-0 text-gray-800 hover:text-orange-600" title={l.title}>{l.title || l.listingId}</a>
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right">{l.units}</td>
                <td className="px-3 py-1.5 text-right">{fmtMoney(l.revenue, cur)}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{fmtMoney(l.fees, cur)}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{fmtMoney(l.listingFees, cur)}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{l.offsite ? fmtMoney(l.offsite, cur) : "–"}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{fmtMoney(l.cost, cur)}{!l.costKnown && <span className="ml-1 text-amber-600" title="Bazı satışların maliyeti bilinmiyor">*</span>}</td>
                <td className={clsx("px-3 py-1.5 text-right font-semibold", signColor(l.profit))}>{fmtMoney(l.profit, cur)}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{fmtPct(l.margin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Note>İşlem ücreti ilana, sipariş ücretleri (offsite, düzenleyici, kargo işlem) siparişteki ciro payına göre dağıtılır. Ödeme işleme ücreti siparişe bağlanamadığı için ciroya oranla paylaştırılır. {d.adsNote}</Note>
    </div>
  );
}

// ---------------- Ilan sagligi ----------------

const HEALTH_ORDER: Health[] = ["star", "converting", "interest_no_sale", "low_conversion", "low_visibility", "dead", "new"];
const HEALTH_STYLE: Record<Health, string> = {
  star: "bg-emerald-50 text-emerald-700 border-emerald-200",
  converting: "bg-sky-50 text-sky-700 border-sky-200",
  interest_no_sale: "bg-amber-50 text-amber-800 border-amber-200",
  low_conversion: "bg-orange-50 text-orange-700 border-orange-200",
  low_visibility: "bg-gray-50 text-gray-700 border-gray-200",
  dead: "bg-red-50 text-red-700 border-red-200",
  new: "bg-violet-50 text-violet-700 border-violet-200",
};
type HSort = "views" | "viewsPerDay" | "favorites" | "sales90" | "conv90" | "ageDays";

export function HealthTab({ refreshKey }: { refreshKey: number }) {
  const shopId = useActiveShopId();
  const takeRefresh = useRefreshOnce(refreshKey);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Health | "all">("all");
  const [sort, setSort] = useState<HSort>("views");
  // Ayni urunun kopyalari (farkli kargo profili) yan yana
  const [grouped, setGrouped] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Yeniden deneme: ilan -> olusan taslak
  const [retried, setRetried] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["finance-health", shopId, refreshKey], queryFn: () => getJson<HealthData>("health", takeRefresh()) });
  const rows = useMemo(() => {
    const list = (q.data?.listings ?? []).filter((l) => filter === "all" || l.health === filter);
    const val = (l: HealthRow) => (l[sort] ?? -1) as number;
    if (!grouped) return [...list].sort((a, b) => val(b) - val(a));
    // Grup sirasi grubun en iyi degerine gore; grup icinde kargo profili adina gore
    const best = new Map<string, number>();
    for (const l of list) { const g = l.group ?? l.listingId; best.set(g, Math.max(best.get(g) ?? -Infinity, val(l))); }
    return [...list].sort((a, b) => {
      const ga = a.group ?? a.listingId, gb = b.group ?? b.listingId;
      return (best.get(gb)! - best.get(ga)!) || ga.localeCompare(gb) || (a.shippingProfile ?? "").localeCompare(b.shippingProfile ?? "", "tr");
    });
  }, [q.data, filter, sort, grouped]);
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data!;

  async function act(l: HealthRow, action: "deactivate" | "autorenew_off" | "autorenew_on") {
    const text = action === "deactivate" ? `"${l.title.slice(0, 60)}" ilanı Etsy'de PASİFE alınsın mı?` :
      action === "autorenew_off" ? `"${l.title.slice(0, 60)}" için otomatik yenileme KAPATILSIN mı? Süresi dolunca (${l.expiresAt ?? "?"}) yenilenmez.` :
      `"${l.title.slice(0, 60)}" için otomatik yenileme açılsın mı?`;
    if (!window.confirm(text)) return;
    setBusy(l.listingId + action);
    setMsg(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/finance/listings/${l.listingId}/action`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ action }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Hata (${res.status})`);
      setMsg(action === "deactivate" ? "İlan pasife alındı." : "Otomatik yenileme güncellendi.");
      qc.setQueryData<HealthData>(["finance-health", shopId, refreshKey], (old) => old && ({
        ...old,
        listings: action === "deactivate" ? old.listings.filter((x) => x.listingId !== l.listingId)
          : old.listings.map((x) => x.listingId === l.listingId ? { ...x, autoRenew: action === "autorenew_on" } : x),
      }));
    } catch (e) {
      setMsg(`İşlem başarısız: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  /** Satmayan ilan (Noel serisi 2. bolum): silinmez, farkli ana kelimeyle
   *  baslayan yeni baslik + etiketle TASLAK olarak kopyalanir. */
  async function retry(l: HealthRow) {
    if (!window.confirm(`"${l.title.slice(0, 60)}" farklı bir başlık ve etiketlerle yeni taslak olarak kopyalansın mı? Eski ilan olduğu gibi kalır; taslağı inceleyip sen yayınlarsın.`)) return;
    setBusy(l.listingId + "retry");
    setMsg(null);
    try {
      const res = await fetch(`${getBackendUrl()}/api/finance/listings/${l.listingId}/retry`, { method: "POST", headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Hata (${res.status})`);
      setRetried((m) => ({ ...m, [l.listingId]: json.data.draftId as string }));
      setMsg(`Yeni taslak hazır: ${json.data.title}`);
    } catch (e) {
      setMsg(`Taslak oluşturulamadı: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }
  // 2 hafta+ hic favori/satis yok ya da gorunurluk dusuk/olu
  const canRetry = (l: HealthRow) => l.health === "dead" || l.health === "low_visibility" || (l.ageDays >= 14 && l.favorites === 0 && l.sales90 === 0);

  const th = (k: HSort | null, label: string, left = false) => (
    <th className={clsx("px-3 py-2 font-medium", left ? "text-left" : "text-right", k && "cursor-pointer hover:text-gray-800", sort === k && "text-gray-900")} onClick={() => k && setSort(k)}>{label}</th>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("all")} className={clsx("rounded-full border px-3 py-1 text-xs", filter === "all" ? "border-gray-800 bg-gray-800 text-white" : "border-gray-200 bg-white text-gray-600")}>
          Tümü · {d.shop.activeListings}
        </button>
        {HEALTH_ORDER.filter((h) => d.counts[h]).map((h) => (
          <button key={h} onClick={() => setFilter(h)} title={d.labels[h].advice}
            className={clsx("rounded-full border px-3 py-1 text-xs", HEALTH_STYLE[h], filter === h && "ring-2 ring-gray-800 ring-offset-1")}>
            {d.labels[h].label} · {d.counts[h]}
          </button>
        ))}
        <label className="ml-auto flex cursor-pointer items-center gap-1.5 self-center text-xs text-gray-600">
          <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
          Aynı ürünün kopyalarını yan yana göster
        </label>
        <span className="self-center text-xs text-gray-500">
          Mağaza ortalaması: {fmtNum(d.shop.medianViewsPerDay, 1)} görüntül./gün · dönüşüm {fmtPct(d.shop.medianConv)}
        </span>
      </div>
      {filter !== "all" && <Note>{d.labels[filter].advice}</Note>}
      {msg && <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">{msg}</div>}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50 text-xs text-gray-500">
            <tr>
              {th(null, "İlan", true)}{th(null, "Durum", true)}{th("ageDays", "Yaş")}{th("views", "Görüntül.")}{th("viewsPerDay", "/gün")}
              {th("favorites", "Favori")}{th("sales90", "Satış 30/90g")}{th("conv90", "Dönüşüm")}{th(null, "Listeleme 90g")}{th(null, "Oto-yenile", true)}
            </tr>
          </thead>
          <tbody>
            {rows.map((l, i) => (
              <tr key={l.listingId} className={clsx("tabular-nums", grouped && i > 0 && rows[i - 1].group === l.group ? "border-t border-dashed border-gray-100" : "border-t border-gray-200")}>
                <td className="max-w-[420px] px-3 py-2">
                  <div className="flex items-center gap-3">
                    <Thumb src={l.etsyImage ?? l.imageUrl} alt={l.title} large />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        {l.analysisId && <IlhamLink id={l.analysisId} />}
                        <a href={l.url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-1 text-gray-800 hover:text-indigo-700" title={l.title}>
                          <span className="line-clamp-2">{l.title}</span><ExternalLink className="h-3 w-3 shrink-0 text-gray-400" />
                        </a>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                        {l.shippingProfile && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700" title="Etsy kargo profili">
                            <Truck className="h-3 w-3" />{l.shippingProfile}
                          </span>
                        )}
                        {(l.twins ?? 1) > 1 && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700" title="Aynı ürün (aynı SKU ya da başlık) bu kadar ilanda açık">{l.twins} kopya</span>
                        )}
                        {l.sku && <span title="SKU">{l.sku}</span>}
                        {l.price != null && <span>{fmtMoney(l.price)}</span>}
                        <span className="text-gray-400">#{l.listingId}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <span className={clsx("whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px]", HEALTH_STYLE[l.health])} title={d.labels[l.health].advice}>{d.labels[l.health].label}</span>
                </td>
                <td className="px-3 py-1.5 text-right text-gray-600">{l.ageDays}g</td>
                <td className="px-3 py-1.5 text-right">{fmtNum(l.views)}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{fmtNum(l.viewsPerDay, 1)}</td>
                <td className="px-3 py-1.5 text-right">{fmtNum(l.favorites)}{l.favRate != null && l.views >= 20 && <span className="ml-1 text-[11px] text-gray-400">{fmtPct(l.favRate)}</span>}</td>
                <td className="px-3 py-1.5 text-right">{l.sales30}/{l.sales90}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{fmtPct(l.conv90)}</td>
                <td className="px-3 py-1.5 text-right text-gray-600">{l.listingFees90 ? fmtMoney(l.listingFees90) : "–"}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-xs">
                  <span className={l.autoRenew ? "text-gray-700" : "text-gray-400"}>{l.autoRenew ? "Açık" : "Kapalı"}</span>
                  <span className="ml-2 inline-flex gap-1.5">
                    <button disabled={!!busy} onClick={() => act(l, l.autoRenew ? "autorenew_off" : "autorenew_on")} className="text-sky-700 hover:underline disabled:opacity-50">
                      {busy === l.listingId + (l.autoRenew ? "autorenew_off" : "autorenew_on") ? "…" : l.autoRenew ? "Kapat" : "Aç"}
                    </button>
                    {(l.health === "dead" || l.health === "low_visibility") && (
                      <button disabled={!!busy} onClick={() => act(l, "deactivate")} className="text-red-600 hover:underline disabled:opacity-50">
                        {busy === l.listingId + "deactivate" ? "…" : "Pasife al"}
                      </button>
                    )}
                    {retried[l.listingId] ? (
                      <Link to={`/listing-builder?id=${retried[l.listingId]}`} className="text-emerald-700 hover:underline">Taslağı aç</Link>
                    ) : canRetry(l) && (
                      <button disabled={!!busy} onClick={() => retry(l)} title="Farklı başlıkla yeni taslak olarak kopyala (eski ilan kalır)" className="text-indigo-600 hover:underline disabled:opacity-50">
                        {busy === l.listingId + "retry" ? "…" : "Yeni başlıkla dene"}
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Note>Görüntülenme ve favori Etsy'nin anlık verisidir (ilanın ömür boyu toplamı, günde bir güncellenir). Dönüşüm, son 90 günün satışının aynı dönemin tahmini görüntülenmesine (günlük ortalama × gün) oranıdır. Eşikler mağazanın kendi ortalamasına göre belirlenir.</Note>
    </div>
  );
}

// ---------------- Sayfa ----------------

const TABS = [
  { id: "pnl", label: "Kâr-Zarar", icon: Wallet },
  { id: "ads", label: "Reklam", icon: Megaphone },
  { id: "listings", label: "İlan Kârlılığı", icon: Package },
  { id: "health", label: "İlan Sağlığı", icon: HeartPulse },
] as const;
type TabId = (typeof TABS)[number]["id"];

export default function FinancePage() {
  const [tab, setTab] = useState<TabId>(() => {
    try { return (localStorage.getItem("erc-finance-tab") as TabId) || "pnl"; } catch { return "pnl"; }
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const pick = (t: TabId) => {
    setTab(t);
    try { localStorage.setItem("erc-finance-tab", t); } catch { /* yoksay */ }
  };
  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><Wallet className="h-6 w-6 text-orange-500" /> Finans</h1>
          <p className="mt-1 text-sm text-gray-500">Etsy ödeme hesabından kâr-zarar, reklam verimliliği, ilan kârlılığı ve ilan sağlığı</p>
        </div>
        <button onClick={() => setRefreshKey((k) => k + 1)} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw className="h-4 w-4" /> Etsy'den yenile
        </button>
      </div>
      <div className="mb-5 flex gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => pick(t.id)}
            className={clsx("-mb-px flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium", tab === t.id ? "border-orange-500 text-orange-600" : "border-transparent text-gray-500 hover:text-gray-800")}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "pnl" && <PnlTab refreshKey={refreshKey} />}
      {tab === "ads" && <AdsTab refreshKey={refreshKey} />}
      {tab === "listings" && <ListingProfitTab refreshKey={refreshKey} />}
      {tab === "health" && <HealthTab refreshKey={refreshKey} />}
    </div>
  );
}
